/**
 * Directory service — API-first.
 *
 * When VITE_PUBLIC_API_URL is set:
 *   All calls hit the Node + Express + MariaDB backend.
 *   Backend URL map:
 *     GET /businesses          (search, category, district, town, page, limit, sort)
 *     GET /businesses/featured
 *     GET /businesses/latest
 *     GET /businesses/:slug
 *     GET /categories
 *     GET /categories/:slug
 *     GET /locations/districts
 *     GET /locations/districts/:slug
 *     GET /locations/towns?districtSlug=...
 *     POST /leads
 *
 * When VITE_PUBLIC_API_URL is empty → mock data is used so the UI renders in
 * preview mode. When an API IS configured, failures PROPAGATE to the caller —
 * the UI shows an honest error + retry state instead of silently faking data.
 */

import type {
  Business,
  BusinessHour,
  BusinessQuery,
  BusinessQueryResult,
  BusinessSort,
  Category,
  CategoryAlias,
  DirectoryLocation,
  Review,
  Subcategory,
} from "@/types/directory";
import { apiGet, apiGetPaged, apiPost, isApiConfigured, ApiError } from "./http";
import { isSponsoredPlan, isFeaturedPlan } from "@/utils/plans";

/** Re-exported so components can check API mode without importing from http directly */
export { isApiConfigured as isApiConfiguredCheck };
import { businessesPartA } from "@/mocks/businessesPartA";
import { businessesPartB } from "@/mocks/businessesPartB";
import { businessesPartC } from "@/mocks/businessesPartC";
import { businessesPartD } from "@/mocks/businessesPartD";
import { businessesPartE } from "@/mocks/businessesPartE";
import { businessesPartF } from "@/mocks/businessesPartF";
import { categories as demoCategories } from "@/mocks/categories";
import { categoryAliases as demoCategoryAliases } from "@/mocks/categoryAliases";
import { locations as demoLocations } from "@/mocks/locations";
import { businessCredentials } from "@/mocks/businessCredentials";

// ── Demo data ─────────────────────────────────────────────────────────────────

const demoBusinesses: Business[] = [
  ...businessesPartA,
  ...businessesPartB,
  ...businessesPartC,
  ...businessesPartD,
  ...businessesPartE,
  ...businessesPartF,
].map((b) => ({ ...b, credentials: businessCredentials[b.slug] ?? [] }));
const DEFAULT_PAGE_SIZE = 9;
/** The backend hard-caps the public listing `limit` at 50 (see listRules) — never request more. */
const MAX_PAGE_SIZE = 50;

function demoDelay(ms = 120): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

// ── Demo query helpers ────────────────────────────────────────────────────────

function matchesKeyword(biz: Business, keyword: string): boolean {
  const hay = [
    biz.name, biz.tagline, biz.subcategory,
    biz.categorySlug, biz.locationSlug, biz.districtSlug,
    biz.tags.join(" "), biz.services.join(" "),
  ].join(" ").toLowerCase();
  return hay.includes(keyword.trim().toLowerCase());
}

function sortDemo(items: Business[], sort: BusinessSort): Business[] {
  const copy = [...items];
  if (sort === "rating")  return copy.sort((a, b) => b.rating - a.rating);
  if (sort === "newest")  return copy.sort((a, b) => b.id.localeCompare(a.id));
  return copy.sort(
    (a, b) =>
      Number(b.sponsored) - Number(a.sponsored) ||
      Number(b.featured)  - Number(a.featured)  ||
      b.rating - a.rating,
  );
}

function queryDemo(q: BusinessQuery): BusinessQueryResult {
  const {
    keyword = "", category = "", district = "", location = "",
    sort = "relevance", page = 1, pageSize = DEFAULT_PAGE_SIZE,
  } = q;

  const filtered = demoBusinesses.filter((b) => {
    if (category && b.categorySlug !== category) return false;
    if (district && b.districtSlug  !== district) return false;
    if (location && b.locationSlug  !== location)  return false;
    if (keyword  && !matchesKeyword(b, keyword))   return false;
    if (q.sponsored !== undefined && Boolean(b.sponsored) !== q.sponsored) return false;
    if (q.featured  !== undefined && Boolean(b.featured)  !== q.featured)  return false;
    return true;
  });

  const sorted = sortDemo(filtered, sort);
  const total  = sorted.length;
  return { items: sorted.slice((page - 1) * pageSize, page * pageSize), total, page, pageSize };
}

// ── API param builder ─────────────────────────────────────────────────────────

/** Maps frontend BusinessQuery to backend query-string parameters */
function buildBusinessParams(q: BusinessQuery): string {
  const p = new URLSearchParams();
  // Backend uses `search` (not keyword), `town` (not location), `limit` (not pageSize)
  if (q.keyword)  p.set("search",   q.keyword);
  if (q.category) p.set("category", q.category);
  if (q.district) p.set("district", q.district);
  if (q.location) p.set("town",     q.location);   // locationSlug → town slug
  if (q.sort && q.sort !== "relevance") p.set("sort", q.sort);
  if (q.sponsored !== undefined) p.set("sponsored", q.sponsored ? "true" : "false");
  if (q.featured  !== undefined) p.set("featured",  q.featured  ? "true" : "false");
  p.set("page",  String(q.page     ?? 1));
  // Clamp to the API's maximum so an oversized request never triggers a 422.
  p.set("limit", String(Math.min(q.pageSize ?? DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE)));
  return p.toString();
}

// ── Backend response adapters ─────────────────────────────────────────────────

interface BackendBusiness {
  id:           number;
  slug:         string;
  name:         string;
  tagline:      string | null;
  description:  string | null;
  categorySlug: string;
  categoryName: string;
  districtSlug: string;
  districtName: string;
  townSlug:     string | null;
  townName:     string | null;
  planSlug:     string;
  address:      string | null;
  phone:        string | null;
  whatsapp:     string | null;
  email:        string | null;
  website:      string | null;
  mapEmbedUrl:  string | null;
  logoUrl:      string | null;
  coverUrl:     string | null;
  youtube_url:  string | null;
  rating:       number;
  reviewCount:  number;
  isVerified:   boolean;
  isSponsored:  boolean;
  isFeatured:   boolean;
  isOpenNow:    boolean;
  yearsInBusiness: number | null;
  hours:        unknown[];
  gallery:      string[];
  services:     string[];
  tags:         string[];
}

const WEEKDAY_NAMES = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
];

/** Hour rows exactly as the Node API sends them. */
interface BackendHour {
  day?: string | null;
  dayName?: string | null;
  dayOfWeek?: number | null;
  open?: string | null;
  openTime?: string | null;
  close?: string | null;
  closeTime?: string | null;
  closed?: boolean | number | null;
  isClosed?: boolean | number | null;
}

/** Normalize any "H:MM", "HH:MM" or "HH:MM:SS" value into a safe "HH:MM" clock string. */
function toClock(value: string | null | undefined): string {
  if (!value || typeof value !== "string") return "";
  const [hour = "", minute = "00"] = value.split(":");
  if (!hour) return "";
  return `${hour.padStart(2, "0")}:${minute.slice(0, 2).padStart(2, "0")}`;
}

/**
 * The API returns hours as { dayOfWeek, dayName, openTime, closeTime, isClosed }
 * while the UI expects { day, open, close, closed }. Normalize here so every
 * consumer (cards, detail hero, JSON-LD, opening-hours widget) gets ONE shape.
 * Missing/garbage hours are dropped to a safe empty value instead of crashing.
 */
function adaptHours(raw: unknown): BusinessHour[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry, index) => {
    const h = (entry ?? {}) as BackendHour;
    const dayIndex = typeof h.dayOfWeek === "number" ? h.dayOfWeek : index;
    return {
      day: h.day ?? h.dayName ?? WEEKDAY_NAMES[dayIndex] ?? "",
      open: toClock(h.open ?? h.openTime),
      close: toClock(h.close ?? h.closeTime),
      closed: Boolean(h.closed ?? h.isClosed),
    };
  });
}

/** Accept either plain strings or { url | image_url | imageUrl } objects. */
function adaptImageList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object") {
        const o = item as Record<string, unknown>;
        return String(o.url ?? o.image_url ?? o.imageUrl ?? "");
      }
      return "";
    })
    .filter(Boolean);
}

/** Accept either plain strings or objects keyed by the field name (label / tag / name). */
function adaptStringList(raw: unknown, key: string): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object") {
        const o = item as Record<string, unknown>;
        return String(o[key] ?? o.label ?? o.tag ?? o.name ?? "");
      }
      return "";
    })
    .filter(Boolean);
}

function adaptBusiness(b: BackendBusiness): Business {
  return {
    id:             String(b.id),
    slug:           b.slug,
    name:           b.name,
    tagline:        b.tagline ?? "",
    description:    b.description ?? "",
    categorySlug:   b.categorySlug,
    subcategory:    b.categoryName,
    districtSlug:   b.districtSlug,
    locationSlug:   b.townSlug ?? b.districtSlug,
    address:        b.address ?? "",
    phone:          b.phone ?? "",
    whatsapp:       b.whatsapp ?? "",
    email:          b.email ?? "",
    website:        b.website ?? "",
    mapEmbedUrl:    b.mapEmbedUrl ?? "",
    logo:           b.logoUrl ?? "",
    cover:          b.coverUrl ?? "",
    gallery:        adaptImageList(b.gallery),
    videoUrl:       b.youtube_url ?? "",
    rating:         Number(b.rating ?? 0),
    reviewCount:    Number(b.reviewCount ?? 0),
    verified:       b.isVerified,
    // A boost plan (Sponsored / Featured Listing) grants placement automatically.
    // The manual admin flags still act as an override so they can force either on.
    sponsored:      b.isSponsored || isSponsoredPlan(b.planSlug),
    featured:       b.isFeatured || isFeaturedPlan(b.planSlug),
    plan:           (b.planSlug as Business["plan"]) ?? "Free",
    openNow:        b.isOpenNow,
    yearsInBusiness:b.yearsInBusiness ?? undefined,
    hours:          adaptHours(b.hours),
    services:       adaptStringList(b.services, "label"),
    tags:           adaptStringList(b.tags, "tag"),
    credentials:    businessCredentials[b.slug] ?? [],
    reviews:        [],
  };
}

interface BackendCategory {
  id: number; slug: string; name: string;
  icon: string; color: string; description: string | null;
  image?: string | null; businessCount?: number;
  subcategories?: { slug: string; name: string }[];
}

function adaptCategory(c: BackendCategory): Category {
  return {
    id:            String(c.id),
    slug:          c.slug,
    name:          c.name,
    description:   c.description ?? "",
    icon:          c.icon,
    image:         c.image ?? "",
    businessCount: c.businessCount ?? 0,
    subcategories: (c.subcategories ?? []).map((s) => ({ slug: s.slug, name: s.name })),
  };
}

interface BackendDistrict {
  id: number; slug: string; name: string;
  localName: string | null; description: string | null;
  businessCount: number;
  image?: string | null;
  towns?: BackendTown[];
}

interface BackendTown {
  id: number; slug: string; name: string;
  districtSlug: string; districtName: string;
  pinCode: string | null; businessCount: number;
  image?: string | null;
}

function adaptDistrict(d: BackendDistrict): DirectoryLocation {
  return {
    id:            String(d.id),
    slug:          d.slug,
    name:          d.name,
    type:          "district",
    districtSlug:  d.slug,
    businessCount: d.businessCount ?? 0,
    image:         d.image ?? "",
  };
}

function adaptTown(t: BackendTown): DirectoryLocation {
  return {
    id:            String(t.id),
    slug:          t.slug,
    name:          t.name,
    type:          "town",
    districtSlug:  t.districtSlug,
    businessCount: t.businessCount ?? 0,
    image:         t.image ?? "",
  };
}

// ── Public API ────────────────────────────────────────────────────────────────

export async function queryBusinesses(q: BusinessQuery): Promise<BusinessQueryResult> {
  // Demo data is only ever used when no API URL is configured (preview mode).
  // With a live API, failures surface to the caller so the UI can show an
  // honest error + retry state instead of silently faking data.
  if (!isApiConfigured()) {
    await demoDelay();
    return queryDemo(q);
  }
  const params = buildBusinessParams(q);
  const result = await apiGetPaged<BackendBusiness>(`/businesses?${params}`);
  return {
    items:    result.data.map(adaptBusiness),
    total:    result.meta.total,
    page:     result.meta.page,
    pageSize: result.meta.limit,
  };
}

export async function getBusinessBySlug(slug: string): Promise<Business | null> {
  if (!isApiConfigured()) {
    await demoDelay();
    return demoBusinesses.find((b) => b.slug === slug) ?? null;
  }
  try {
    const b = await apiGet<BackendBusiness>(`/businesses/${slug}`);
    return adaptBusiness(b);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export async function getFeaturedBusinesses(limit = 6): Promise<Business[]> {
  if (!isApiConfigured()) {
    const featured = demoBusinesses.filter((b) => b.featured || b.sponsored);
    return (featured.length ? featured : demoBusinesses).slice(0, limit);
  }
  const list = await apiGet<BackendBusiness[]>(`/businesses/featured?limit=${limit}`);
  return list.map(adaptBusiness);
}

/**
 * Sponsored placements only — pulled from a wide pool so the home band fills.
 *
 * The Sponsored band must ALWAYS render on the home page. Order of sources:
 *   1. the live listing pool (first page, relevance-sorted)
 *   2. the dedicated /businesses/featured endpoint
 *   3. the bundled sample businesses (pre-115 behaviour) — so the band is
 *      never blank, even when the live API is down or has no sponsored rows.
 */
export async function getSponsoredBusinesses(limit = 12): Promise<Business[]> {
  const demoSponsored = demoBusinesses.filter((b) => b.sponsored);

  if (!isApiConfigured()) {
    return demoSponsored.slice(0, limit);
  }

  try {
    const result = await queryBusinesses({ sort: "relevance", page: 1, pageSize: MAX_PAGE_SIZE });
    const sponsored = result.items.filter((b) => b.sponsored);
    if (sponsored.length > 0) return sponsored.slice(0, limit);
  } catch {
    /* fall through to the dedicated featured endpoint */
  }

  try {
    const list = await apiGet<BackendBusiness[]>(`/businesses/featured?limit=${MAX_PAGE_SIZE}`);
    const sponsored = list.map(adaptBusiness).filter((b) => b.sponsored);
    if (sponsored.length > 0) return sponsored.slice(0, limit);
  } catch {
    /* fall through to the bundled samples */
  }

  return demoSponsored.slice(0, limit);
}

/**
 * Featured (non-sponsored) placements only — keeps the featured band distinct.
 * Same guaranteed-render order as getSponsoredBusinesses, ending in the
 * bundled sample businesses so the band never disappears.
 */
export async function getFeaturedOnlyBusinesses(limit = 12): Promise<Business[]> {
  const demoFeatured = demoBusinesses.filter((b) => b.featured && !b.sponsored);

  if (!isApiConfigured()) {
    return demoFeatured.slice(0, limit);
  }

  try {
    const result = await queryBusinesses({ sort: "relevance", page: 1, pageSize: MAX_PAGE_SIZE });
    const featured = result.items.filter((b) => b.featured && !b.sponsored);
    if (featured.length > 0) return featured.slice(0, limit);
  } catch {
    /* fall through to the dedicated featured endpoint */
  }

  try {
    const list = await apiGet<BackendBusiness[]>(`/businesses/featured?limit=${MAX_PAGE_SIZE}`);
    const featured = list.map(adaptBusiness).filter((b) => b.featured && !b.sponsored);
    if (featured.length > 0) return featured.slice(0, limit);
  } catch {
    /* fall through to the bundled samples */
  }

  return demoFeatured.slice(0, limit);
}

export async function getLatestBusinesses(limit = 6): Promise<Business[]> {
  if (!isApiConfigured()) {
    await demoDelay();
    return [...demoBusinesses].reverse().slice(0, limit);
  }
  const list = await apiGet<BackendBusiness[]>(`/businesses/latest?limit=${limit}`);
  return list.map(adaptBusiness);
}

export async function getRelatedBusinesses(business: Business, limit = 3): Promise<Business[]> {
  if (!isApiConfigured()) {
    return demoBusinesses
      .filter(
        (b) =>
          b.slug !== business.slug &&
          (b.categorySlug === business.categorySlug || b.districtSlug === business.districtSlug),
      )
      .slice(0, limit);
  }
  const params = new URLSearchParams({
    category: business.categorySlug,
    district: business.districtSlug,
    limit:    String(limit + 1),
  });
  const result = await apiGetPaged<BackendBusiness>(`/businesses?${params}`);
  return result.data
    .map(adaptBusiness)
    .filter((b) => b.slug !== business.slug)
    .slice(0, limit);
}

/**
 * Categories for the public site (home tiles, footer strip, the “Essentials”
 * /categories grid).
 *
 * The live `/categories` endpoint is the source of truth, but when it is empty
 * (or the call fails) the page must never render a blank grid — so we fall back
 * to the bundled 20 main categories. Real backend rows always win when present.
 */
export async function getCategories(): Promise<Category[]> {
  if (!isApiConfigured()) return demoCategories;
  try {
    const list = await apiGet<BackendCategory[]>("/categories");
    const mapped = list.map(adaptCategory);
    if (mapped.length > 0) return mapped;
  } catch {
    /* fall through to the bundled categories */
  }
  return demoCategories;
}

export async function getCategoryBySlug(slug: string): Promise<Category | null> {
  // Keep the bundled copy on hand so a category linked from the (fallback)
  // Essentials grid never dead-ends on a “Category not found” page.
  const bundled = demoCategories.find((c) => c.slug === slug) ?? null;
  if (!isApiConfigured()) return bundled;
  try {
    const cat = await apiGet<BackendCategory>(`/categories/${slug}`);
    return adaptCategory(cat);
  } catch (err) {
    if (bundled) return bundled;
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** Sub categories for a single category (empty when the category has none). */
export async function getSubcategories(categorySlug: string): Promise<Subcategory[]> {
  if (!categorySlug) return [];
  const category = await getCategoryBySlug(categorySlug);
  return category?.subcategories ?? [];
}

/**
 * Admin-managed A–Z name → branded slug overrides. The A–Z index page uses
 * these to link a keyword-level category name straight to its curated branded
 * page. Empty when none are configured (falls back to auto-matching).
 */
export async function getCategoryAliases(): Promise<CategoryAlias[]> {
  if (!isApiConfigured()) return demoCategoryAliases;
  return apiGet<CategoryAlias[]>("/categories/aliases");
}

export async function getLocations(): Promise<DirectoryLocation[]> {
  if (!isApiConfigured()) return demoLocations;
  const [districts, towns] = await Promise.all([
    apiGet<BackendDistrict[]>("/locations/districts"),
    apiGet<BackendTown[]>("/locations/towns"),
  ]);
  return [
    ...districts.map(adaptDistrict),
    ...towns.map(adaptTown),
  ];
}

export async function getDistricts(): Promise<DirectoryLocation[]> {
  if (!isApiConfigured()) return demoLocations.filter((l) => l.type === "district");
  const list = await apiGet<BackendDistrict[]>("/locations/districts");
  return list.map(adaptDistrict);
}

export async function getTownsByDistrict(districtSlug: string): Promise<DirectoryLocation[]> {
  if (!isApiConfigured()) {
    return demoLocations.filter((l) => l.type === "town" && l.districtSlug === districtSlug);
  }
  const list = await apiGet<BackendTown[]>(
    `/locations/towns?districtSlug=${encodeURIComponent(districtSlug)}`,
  );
  return list.map(adaptTown);
}

export async function getLocationBySlug(slug: string): Promise<DirectoryLocation | null> {
  const all = await getLocations();
  return all.find((l) => l.slug === slug) ?? null;
}

// ── Lead / form submission ────────────────────────────────────────────────────

export interface LeadPayload {
  formType:   "enquiry" | "listing" | "contact";
  name:       string;
  email?:     string;
  phone?:     string;
  subject?:   string;
  message:    string;
  businessId?: number;
  budget?:    string;
  sourcePage?: string;
}

export async function submitLead(payload: LeadPayload): Promise<{ id: number }> {
  if (isApiConfigured()) {
    return apiPost<{ id: number }>("/leads", payload);
  }
  // Demo mode — simulate success
  await demoDelay(300);
  return { id: Math.floor(Math.random() * 9999) };
}

// ── Customer reviews ──────────────────────────────────────────────────────────

export interface ReviewPayload {
  businessId: number;
  rating:     number;
  authorName: string;
  comment?:   string;
}

interface BackendReview {
  id:         number;
  rating:     number;
  comment:    string | null;
  authorName: string;
  isVerified: boolean | number;
  status:     string;
  createdAt:  string;
  reviewedAt: string | null;
}

function adaptReview(r: BackendReview): Review {
  const dateSource = r.reviewedAt ?? r.createdAt;
  return {
    id:       String(r.id),
    author:   r.authorName,
    rating:   r.rating,
    date:     new Date(dateSource).toLocaleDateString("en-IN", {
      year: "numeric", month: "long", day: "numeric",
    }),
    comment:  r.comment ?? "",
    verified: Boolean(r.isVerified),
  };
}

/** Submit a customer review — enters the moderation queue. */
export async function submitReview(
  payload: ReviewPayload,
): Promise<{ id: number; status: string }> {
  if (isApiConfigured()) {
    return apiPost<{ id: number; status: string }>("/reviews", payload);
  }
  await demoDelay(320);
  return { id: Math.floor(Math.random() * 9999), status: "pending" };
}

/** Fetch approved reviews for a business. */
export async function getBusinessReviews(businessId: string): Promise<Review[]> {
  if (!isApiConfigured()) return [];
  const result = await apiGetPaged<BackendReview>(
    `/reviews?businessId=${encodeURIComponent(businessId)}&status=approved&limit=20`,
  );
  return result.data.map(adaptReview);
}

/** Record a public profile view for a business (best-effort, never throws). */
export async function recordBusinessView(businessId: number | string): Promise<void> {
  if (!isApiConfigured()) return;
  try {
    await apiPost(`/businesses/${businessId}/view`, {});
  } catch {
    /* analytics ping — safe to ignore */
  }
}